import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Dropdown from 'react-bootstrap/Dropdown'
import queryString from 'query-string'
import styled from 'styled-components/macro'
import {
  SearchBar,
  SearchIcon,
  SearchInput,
  SearchInputContainer,
} from 'src/components/Reusable/Search/Search.styles'
import { CheckBoxInput } from 'src/components/Reusable/Inputs/CheckBox'
import { PERMISSIONS } from 'src/config/roleConfig'
import { useDocumentRequests } from 'src/components/async/DocumentRequest'
import { useDRLRequest } from 'src/pages/Requests/requests.hooks'
import { useResolveSourceDisplayNames } from 'src/pages/Requests/useResolveSourceDisplayNames'
import { useNavigateContext } from '../../../Navigation/NavigateContext'
import {
  FilterCountChip,
  FilterDropdownItem,
  FilterDropdownToggle,
} from '../FilterBar.styles'
import { FilterName, FilterParam } from '../FilterBar.types'
import { useFilterLabels } from '../FilterLabelsContext'
import { DropDownMenu } from './FilterDropdown.styles'
import { LoadingFilterDropdown } from './FilterDropdown.components'

const SearchContainer = styled.div`
  padding: 8px 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors?.border ?? '#e5e7eb'};

  ${SearchBar} {
    width: 100%;
  }
`

export const RequestSourceFilterDropdown = React.memo(
  ({ isNested = false }: { isNested?: boolean }): JSX.Element => {
    const location = useLocation()
    const navigate = useNavigate()
    const [searchQuery, setSearchQuery] = useState('')
    const { registerFilter } = useFilterLabels()
    const {
      navigateState: { parsedQueryParams },
    } = useNavigateContext()

    const request = useDRLRequest()
    const { data, isLoading } = useDocumentRequests({ request })
    const requests = useMemo(() => data?.getItemsList() ?? [], [data])
    const displayNames = useResolveSourceDisplayNames(requests)

    const allOptions = useMemo(() => {
      const seenSrc = new Set<string>()
      const seenLabel = new Set<string>()
      const values: { value: string; label: string }[] = []
      requests.forEach((r) => {
        const src = r.getSource()
        if (!src || seenSrc.has(src)) return
        seenSrc.add(src)
        const label = displayNames.get(src)
        if (!label || seenLabel.has(label)) return
        seenLabel.add(label)
        values.push({ value: src, label })
      })
      return values.sort((a, b) => a.label.localeCompare(b.label))
    }, [requests, displayNames])

    const searchParamsFallback = useMemo(
      () => queryString.parse(location.search, { arrayFormat: 'bracket' }),
      [location.search],
    )
    const searchParams = parsedQueryParams ?? searchParamsFallback
    const appliedFilters: string[] = useMemo(() => {
      const raw = searchParams[FilterParam.REQUEST_SOURCE]
      return Array.isArray(raw) ? raw : raw ? [raw] : []
    }, [searchParams])

    const filteredOptions = useMemo(() => {
      const q = searchQuery.trim().toLowerCase()
      return q
        ? allOptions.filter((opt) => opt.label.toLowerCase().includes(q))
        : allOptions
    }, [allOptions, searchQuery])

    useEffect(() => {
      const valueMap: Record<string, string> = {}
      allOptions.forEach((opt) => {
        valueMap[opt.value] = opt.label
      })
      registerFilter(
        FilterParam.REQUEST_SOURCE,
        FilterName.REQUEST_SOURCE,
        valueMap,
      )
    }, [allOptions, registerFilter])

    if (isLoading) {
      return (
        <LoadingFilterDropdown
          filterName={FilterName.REQUEST_SOURCE}
          isNested={isNested}
        />
      )
    }

    return (
      <Dropdown autoClose="outside">
        <Dropdown.Toggle
          as={FilterDropdownToggle}
          id="request-source-filter-dropdown"
          $hasSelection={appliedFilters.length > 0}
          $isNested={isNested}
        >
          <p>{FilterName.REQUEST_SOURCE}</p>
          {appliedFilters.length > 0 && (
            <FilterCountChip ml="xxs">{appliedFilters.length}</FilterCountChip>
          )}
        </Dropdown.Toggle>
        <DropDownMenu>
          <SearchContainer>
            <SearchBar>
              <SearchInputContainer>
                <SearchIcon />
                <SearchInput
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search"
                  autoFocus
                />
              </SearchInputContainer>
            </SearchBar>
          </SearchContainer>
          {filteredOptions.map((opt) => {
            const isChecked = appliedFilters.includes(opt.value)
            const newFilters = isChecked
              ? appliedFilters.filter((f) => f !== opt.value)
              : [...appliedFilters, opt.value]
            const href = queryString.stringifyUrl(
              {
                url: location.pathname,
                query: {
                  ...searchParams,
                  [FilterParam.REQUEST_SOURCE]: newFilters,
                },
              },
              { arrayFormat: 'bracket' },
            )
            return (
              <FilterDropdownItem key={opt.value} replace to={href}>
                <CheckBoxInput
                  id={opt.value}
                  label={<span title={opt.label}>{opt.label}</span>}
                  checked={isChecked}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => navigate(href, { replace: true })}
                  tabIndex={-1}
                  requiredPermissions={[PERMISSIONS.READ]}
                />
              </FilterDropdownItem>
            )
          })}
        </DropDownMenu>
      </Dropdown>
    )
  },
)
